/// <reference types="vite/client" />
import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking
 * Only initializes in production when DSN is provided
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize in production with a valid DSN
  if (import.meta.env.PROD && dsn) {
    Sentry.init({
      dsn,
      environment: import.meta.env.VITE_APP_ENV || 'production',
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
      tracesSampleRate: 0.1, // 10% of transactions
      replaysSessionSampleRate: 0.1, // 10% of sessions
      beforeSend(event: Sentry.ErrorEvent) {
        // Filter out development errors if they somehow get through
        if (event.environment === 'development') {
          return null;
        }
        return event;
      },
    });
  }
};

/**
 * Capture a realtime-specific error with context
 * @param error - The error object
 * @param context - Additional context for debugging
 */
export const captureRealtimeError = (
  error: Error,
  context?: Record<string, unknown>
) => {
  // Only capture in production
  if (!import.meta.env.PROD) {
    console.error('[Realtime Error]', error, context);
    return;
  }

  Sentry.captureException(error, {
    tags: {
      component: 'realtime',
      feature: 'supabase_realtime',
    },
    extra: {
      ...context,
      timestamp: new Date().toISOString(),
    },
  });
};

/**
 * Capture a realtime connection status change
 * @param status - The new connection status
 * @param channelName - The channel name
 * @param details - Additional details
 */
export const captureRealtimeStatus = (
  status: 'connected' | 'disconnected' | 'error' | 'reconnecting',
  channelName: string,
  details?: Record<string, unknown>
) => {
  // Only track connection issues and errors in production
  if (!import.meta.env.PROD || status === 'connected') {
    return;
  }

  Sentry.addBreadcrumb({
    category: 'realtime',
    message: `Realtime ${status}: ${channelName}`,
    level: status === 'error' ? 'error' : 'warning',
    data: {
      channelName,
      status,
      ...details,
    },
  });
};
